import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import PillarCard from "../components/PillarCard";
import {
  ChartLineUp,
  Storefront,
  Stack,
  Cube,
  Compass,
  Megaphone,
  ArrowRight,
} from "@phosphor-icons/react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/6d7cad75-b3f1-423c-a893-00a3231cc41c/images/7c01e612c4c75d24bafa9965de9a5392aae80905fdf002d8760b5b9d6b5bef3e.png";
const RETAIL_ARCH =
  "https://customer-assets.emergentagent.com/job_modern-merchandising-1/artifacts/ctl3aug9_Screenshot%202026-05-04%20at%2013.26.02.png";
const STORE_AISLE =
  "https://customer-assets.emergentagent.com/job_modern-merchandising-1/artifacts/dq1qvy9g_Screenshot%202026-05-04%20at%2013.23.03.png";

const Hero = () => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden border-b border-[#e5e5e5] bg-white" data-testid="hero-section">
      <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-10 md:pb-16 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7 gm-rise">
            <div className="flex items-center gap-3">
              <span className="w-10 h-px bg-[#0A4DFF]" />
              <span className="gm-eyebrow text-[#0A4DFF]">{t.home.eyebrow}</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[5.25rem] leading-[0.95] tracking-tighter mt-8 font-medium">
              {t.home.heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-xl leading-relaxed">
              {t.home.heroSub}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link to="/instore-data" className="gm-btn gm-btn-primary" data-testid="hero-cta-instore">
                {t.home.heroCta1} <ArrowRight size={16} />
              </Link>
              <a href="#pillars" className="gm-btn gm-btn-outline" data-testid="hero-cta-services">
                {t.home.heroCta2}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-[#e5e5e5] bg-[#0a0a0a]">
              <img
                src={HERO_BG}
                alt="Abstract retail data graphic"
                className="w-full h-full object-cover opacity-90"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <div className="font-mono text-[10px] tracking-widest text-white/70">QLIKVIEW · LIVE</div>
                <div className="font-display text-white text-2xl mt-1">SYMES → MERVIK → DASHBOARD</div>
              </div>
              <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                <span className="text-white/80 text-[10px] font-mono tracking-widest">EAN · 859×7240×0021</span>
                <span className="w-2 h-2 rounded-full bg-[#0A4DFF] animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t border-[#e5e5e5]" data-testid="home-stats">
          {t.home.stats.map((s) => (
            <div key={s.v} className="border-b md:border-b-0 md:border-r border-[#e5e5e5] py-8 pr-6 last:border-r-0">
              <div className="font-display text-3xl md:text-4xl tracking-tight">{s.k}</div>
              <div className="text-xs text-[#525252] uppercase tracking-widest mt-2 font-mono">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PillarsSection = () => {
  const { t } = useLang();
  const pillars = [
    {
      to: "/merchandising-services",
      kicker: t.pillars.merchandising.kicker,
      title: t.pillars.merchandising.title,
      desc: t.pillars.merchandising.desc,
      Icon: Storefront,
      testid: "pillar-merchandising",
    },
    {
      to: "/instore-data",
      kicker: t.pillars.instore.kicker,
      title: t.pillars.instore.title,
      desc: t.pillars.instore.desc,
      Icon: ChartLineUp,
      feature: true,
      image:
        "https://static.prod-images.emergentagent.com/jobs/6d7cad75-b3f1-423c-a893-00a3231cc41c/images/4f419fc0f102634deeade9fe8a9a4539c40f60c586163ef5ad06d74d79f727a5.png",
      imageAlt: "MERVIK mobile dashboard for instore data",
      testid: "pillar-instore",
    },
    {
      to: "/shelfmasters",
      kicker: t.pillars.shelfmasters.kicker,
      title: t.pillars.shelfmasters.title,
      desc: t.pillars.shelfmasters.desc,
      Icon: Stack,
      testid: "pillar-shelfmasters",
    },
    {
      to: "/retail-digitalisation",
      kicker: t.pillars.digital.kicker,
      title: t.pillars.digital.title,
      desc: t.pillars.digital.desc,
      Icon: Cube,
      testid: "pillar-digital",
    },
    {
      to: "/retail-consulting",
      kicker: t.pillars.consulting.kicker,
      title: t.pillars.consulting.title,
      desc: t.pillars.consulting.desc,
      Icon: Compass,
      testid: "pillar-consulting",
    },
    {
      to: "/pos",
      kicker: t.pillars.pos.kicker,
      title: t.pillars.pos.title,
      desc: t.pillars.pos.desc,
      Icon: Megaphone,
      testid: "pillar-pos",
    },
  ];

  return (
    <section id="pillars" className="pt-14 md:pt-20 pb-8 md:pb-12 bg-[#fafafa]" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <span className="gm-eyebrow">{t.home.pillarsEyebrow}</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium mt-5 leading-[1]">
              {t.home.pillarsTitle}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#525252] leading-relaxed text-base md:text-lg">{t.home.pillarsSub}</p>
          </div>
        </div>

        {/* Asymmetric bento — 6 pillars */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-10">
          {/* Row 1: feature + 2 small */}
          <PillarCard {...pillars[1]} className="md:col-span-7 md:row-span-2 min-h-[420px]" />
          <PillarCard {...pillars[0]} className="md:col-span-5 min-h-[200px]" />
          <PillarCard {...pillars[2]} className="md:col-span-5 min-h-[200px]" />

          {/* Row 2: 3 cards */}
          <PillarCard {...pillars[3]} className="md:col-span-4 min-h-[220px]" />
          <PillarCard {...pillars[4]} className="md:col-span-4 min-h-[220px]" />
          <PillarCard {...pillars[5]} className="md:col-span-4 min-h-[220px]" />
        </div>
      </div>
    </section>
  );
};

const Marquee = () => {
  const items = [
    "Price Monitoring",
    "Shelf Monitoring",
    "Secondary Placements",
    "POS Materials",
    "MERVIK",
    "SYMES",
    "QlikView",
    "Field Audits",
    "Planogram Compliance",
    "OOS Detection",
  ];
  const row = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-[#e5e5e5] bg-white py-6" data-testid="home-marquee">
      <div className="flex gap-12 gm-marquee-track whitespace-nowrap">
        {row.map((it, i) => (
          <div key={i} className="flex items-center gap-12">
            <span className="font-display text-2xl md:text-3xl text-[#0a0a0a]">{it}</span>
            <span className="w-2 h-2 bg-[#0A4DFF]" />
          </div>
        ))}
      </div>
    </div>
  );
};

const Quote = () => {
  const { t } = useLang();
  return (
    <section className="py-14 md:py-20 bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="home-quote">
      <div className="absolute inset-0 opacity-30">
        <img src={STORE_AISLE} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0a0a0a]/70" />
      </div>
      <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
        <span className="gm-eyebrow text-white/50">// MANIFEST</span>
        <h3 className="font-display text-3xl md:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
          {t.home.ctaTitle}
        </h3>
        <p className="text-white/70 mt-6 text-lg max-w-2xl leading-relaxed">{t.home.ctaSub}</p>
        <Link
          to="/kontakt"
          className="gm-btn gm-btn-primary mt-10"
          data-testid="home-cta-button"
        >
          {t.home.ctaButton} <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
};

const ImageStrip = () => (
  <section className="pt-2 pb-10 bg-[#fafafa]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="aspect-[4/3] overflow-hidden border border-[#e5e5e5]">
        <img src={RETAIL_ARCH} alt="Bohemia Sekt branded retail display by Global Marketing" className="w-full h-full object-cover" />
      </div>
      <div className="aspect-[4/3] overflow-hidden border border-[#e5e5e5]">
        <img src={STORE_AISLE} alt="Supermarket refrigerated aisle — shelf and price monitoring" className="w-full h-full object-cover" />
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title={{
          cs: "Global Marketing · Future of retail",
          en: "Global Marketing · Future of retail",
        }}
        description={{
          cs:
            "Global Marketing — vlastní systémy MERVIK, SYMES a QlikView pro instore data, merchandising, POS, Shelfmasters a Reponio Store. CZ + SK.",
          en:
            "Global Marketing — proprietary MERVIK, SYMES and QlikView systems for instore data, merchandising, POS, Shelfmasters and Reponio Store. CZ + SK.",
        }}
        path="/"
      />
      <Hero />
      <Marquee />
      <PillarsSection />
      <ImageStrip />
      <Quote />
    </div>
  );
}
